import React, {
    useMemo,
    useState,
    useEffect,
    useCallback
} from "react";
import {
    TooltipHost,
    DirectionalHint
} from "@fluentui/react";
import { toast } from "react-toastify";
import styles from "./JsonGridInvoice.module.css";
import {
    toggleItems,
    get_grid_data,
    prePreJsonInv,
    getValueAtPath,
    setValueAtPath,
    pathToBreadcrumb,
    deleteValueAtPath,
    sortObjectByKeyOrder,
    getContentPathWithId
} from "../../util_glob";
import { computeInvoiceExciseSum } from "./InvoiceItemsUtil";
import ValidateFieldWrap, { getFieldValidation } from "./ValidateFieldWrap";

import { TbMoodLookUp } from "react-icons/tb";
import { invWarnDynTooltip } from "../../styles_glob";
import { MdDelete } from "react-icons/md";
import { BsChevronExpand } from "react-icons/bs";
import { FaChevronCircleUp } from "react-icons/fa";
import { FiDownload } from "react-icons/fi";
import { IoAddCircleOutline } from "react-icons/io5";
import { FaChevronCircleDown } from "react-icons/fa";
import {BiCustomize, BiSolidUpArrowSquare} from "react-icons/bi";
import {
    gextrprop,
    gloggedinuser,
    gselectedgroup,
    empty_extraction_field_list,
    gcustominvoiceinstructionscallout,
} from "../../interfaces";
import {
    finFieldList,
    invoice_togglebles,
    invoice_item_order,
    invoice_key_renames,
    invoice_hidden_fields,
    invoice_non_editables,
    inv_main_tbl_tooltips,
    invMainListDividerFlag,
    invMainFieldToGroupMap,
    invMainFieldsWCitationLink,
    collapsableGroupsDefaultState,
    localStorageInvCollapsebleKey,
    finField
} from "../../lsts";
import {downloadPDF} from "../../api";
import {qGetInvInstrct} from "../../api/qpi";
import { TbBrowserShare } from "react-icons/tb";

{/*
    Invoice Main (non-item) Grid
    Shows invoice main grid - left side of the PDF viewer
        ⇒  To config hidden fields - invoice_hidden_fields

*/}

interface JsonGridInvoiceProps {
    fileid: string;
    filename: string;
    invdata: any;
    loggedInUser?: gloggedinuser;
    onDataHighlight?: (searchString: string) => void;
    listRefreshRequest?: () => void;
    group?: gselectedgroup;
    sendBackData?: (gridData: any) => void;
    transitTotalRowArrMap?: Map<string, any[]>; // incoming data changes from inv items table
    isVersionView?: boolean; // 🕰️ viewing a previous version — hide file-scoped actions (download / copy link)
}

const JsonGridInvoice: React.FC<JsonGridInvoiceProps> = ({ fileid, filename, invdata, loggedInUser, onDataHighlight, listRefreshRequest, group, sendBackData, transitTotalRowArrMap, isVersionView }) => {
    const [fid, setFid] = useState<string>(fileid || "");
    const [itmName, setItmName] = useState<string>(filename || "");
    const [gridData, setGridData] = useState(get_grid_data(invdata)); // invoice data
    const [hasUnsaved, setHasUnsaved] = useState<boolean>(false);
    const [isExpanded, setIsExpanded] = useState<boolean>(true);
    const [realTimeInvItemTotals, setRealTimeInvItemTotals] = useState<any[]>(transitTotalRowArrMap?.get(fileid) || []); // current inv item total row array
    const [fieldsTurnedOff, setFieldsTurnedOff] = useState<string[]>([...invoice_hidden_fields, ...prePreJsonInv(group)]); // hidden fields
    const [isInstructionCalloutOpen, setIsInstructionCalloutOpen] = useState<gcustominvoiceinstructionscallout>();
    const [instructCalloutTargetElement, setInstructCalloutTargetElement] = useState<HTMLDivElement | null>(null);
    const [totalsWarnInternalMap, setTotalsWarnInternalMap] = useState<Map<string, any>>(new Map());
    const [invCustomFields, setInvCustomFields] = useState<gextrprop[]>(group?.extractionfieldlist?.custom_props || []);
    const [fieldsGroupedWCust, setFieldsGroupedWCust] = useState<Map<string, string>>(invMainFieldToGroupMap);

    const allowedDeviation: number = useMemo(() => {
        try {
            return group?.extractionfieldlist?.allowed_deviation || 0.05
        } catch (e) {
            return 0.05
        }
    }, [fileid]);

    // see computeInvoiceExciseSum in InvoiceItemsUtil.ts — reconciles Σ(item totals) against
    // invoice_total_net/gross for invoices where excise is a VAT-taxable-base component that
    // individual items don't carry.
    const exciseSum: number = useMemo(() => computeInvoiceExciseSum(gridData['invoice_items']), [gridData]);

    const [sectionCollapseState, setSectionCollapseState] = useState<Map<string, boolean>>(
        localStorage.getItem(localStorageInvCollapsebleKey) ?
            new Map<string, boolean>(JSON.parse(localStorage.getItem(localStorageInvCollapsebleKey) || '{}'))
            : new Map<string, boolean>(collapsableGroupsDefaultState)
    );

    useEffect(() => {
        setFid(fileid);
        if (group?.extractionfieldlist?.enable_custom_props == 1){
            setInvCustomFields(group?.extractionfieldlist?.custom_props || []);
        } else {
            setInvCustomFields([]);
        }
        for (const cp of group?.extractionfieldlist?.custom_props || []){
            if (cp.global_prop == true) invMainFieldToGroupMap.set(cp.name?.replace(" ", "_"), "Custom Properties");
        }

        setFieldsGroupedWCust(invMainFieldToGroupMap);
        setGridData(get_grid_data(invdata));

        setItmName(fileid);
        if (fileid !== fid) {
            setHasUnsaved(false);
            setTotalsWarnInternalMap(new Map());
        }
    }, [invdata, fileid]);

    const orderListWithCustom = useMemo(() => {
        return [...invoice_item_order, ...invCustomFields.filter(cp => cp.global_prop == true).map(cp => cp.name?.replace(" ", "_") || "")];
    }, [invCustomFields]);

    useEffect(() => {
        // user updates inv item cells
        setRealTimeInvItemTotals(transitTotalRowArrMap?.get(fileid?.toString() ?? '0') || []);

        handleFieldChange(['calculated_invoice_items_total'], (transitTotalRowArrMap?.get(fileid?.toString() ?? '0')?.[0]?.['total_price'] ?? 0).toString());
    }, [transitTotalRowArrMap]);

    useEffect(() => {
        if (hasUnsaved && sendBackData) sendBackData(gridData);

        let totNet      = gridData['invoice_total_net'] ? parseFloat(gridData['invoice_total_net']) : 0;
        let totGrss     = gridData['invoice_total'] ? parseFloat(gridData['invoice_total']) : 0;
        let calcItmTot  = gridData['calculated_invoice_items_total'] ? parseFloat(gridData['calculated_invoice_items_total']) : 0;
        let vat         = gridData['vat'] ? parseFloat(gridData['vat']) : 0;
        let excise      = gridData['excise'] ? parseFloat(gridData['excise']) : 0;

        calcItmTot += computeInvoiceExciseSum(gridData['invoice_items']);

        let missNet         = Math.abs(totNet - calcItmTot)
        let missGrs         = Math.abs(totGrss - calcItmTot)
        let NetVatNotGrand  = Math.abs((totNet + vat + excise) - totGrss)

        let prepMap = new Map<string, any>()

        if (missGrs > allowedDeviation && missNet > allowedDeviation) {
            prepMap.set('invoice_total_net', {
                'missGrs': missGrs,
                'missNet': missNet,
                'NetVatNotGrand': NetVatNotGrand,
                'totNet': totNet,
                'totGrss': totGrss,
                'calcItmTot': calcItmTot,
                'vat': vat
            })
            prepMap.set('calculated_invoice_items_total', {
                'missGrs': missGrs,
                'missNet': missNet,
                'NetVatNotGrand': NetVatNotGrand,
                'totNet': totNet,
                'totGrss': totGrss,
                'calcItmTot': calcItmTot,
                'vat': vat
            })
        }

        if (NetVatNotGrand > allowedDeviation) prepMap.set('vat', {
            'missGrs': missGrs,
            'missNet': missNet,
            'NetVatNotGrand': NetVatNotGrand,
            'totNet': totNet,
            'totGrss': totGrss,
            'calcItmTot': calcItmTot,
            'vat': vat
        })

        setTotalsWarnInternalMap(prepMap)

    }, [gridData]);

    const fieldsToShow = useMemo(() => {
        let filds: finField[] = []
        try {
            filds = finFieldList.filter(field => !(((group?.extractionfieldlist || empty_extraction_field_list).excl || ([] as string[])).includes(field.id)));

            if (group?.extractionfieldlist && group?.extractionfieldlist?.enable_custom_props == 1 && Array.isArray(group?.extractionfieldlist?.custom_props)) {
                const customProps = group?.extractionfieldlist?.custom_props?.map((cp: gextrprop) => ({
                    id: cp?.name?.toLowerCase() || '',
                    name: cp?.name || '',
                    level: (cp?.global_prop ? "main" : "item" as "main" | "item"),
                    description: cp?.description || '',
                    systemDisabled: false
                }))
                return [
                    ...filds,
                    ...customProps
                ];
            } else {
                return filds;
            }
        } catch (e) {
            return [];
        }
    }, [group, gridData]);

    const itemFields = useMemo(() => fieldsToShow?.filter(field => field?.level === "item").map(field => field?.id), [group, gridData]);

    const collapsableGroups = useMemo(() => {
        let dividerMapDynamic = new Map<string, string>(invMainListDividerFlag);
        let name              = invCustomFields.find(cp => cp.global_prop == true)?.name?.replace(" ", "_") || ""
        if (name != '') dividerMapDynamic.set(name, "Custom Properties");

        return dividerMapDynamic;
    }, [invCustomFields]);

    const handleAddItem = (path: (string | number)[]) => {
        const arrayAtPath = getValueAtPath(gridData, path);
        if (Array.isArray(arrayAtPath)) {
            const newItem       = Object.fromEntries(itemFields.map(key => [key, ""]));
            const updatedArray  = [...arrayAtPath, newItem];
            const updatedData   = setValueAtPath(gridData, path, updatedArray);
            setGridData(updatedData);
            setHasUnsaved(true);

            //if (listRefreshRequest) listRefreshRequest();
        } else {
            console.error("‹value_path_not_array›");
        }
    };

    const handleFieldChange = (path: (string | number)[], newValue: string) => {

        const updatedData = setValueAtPath(gridData, path, newValue);
        setGridData(updatedData);
        setHasUnsaved(true);
    };

    const handleDelete = (path: (string | number)[]) => {
        const updatedData = deleteValueAtPath(gridData, path);
        setGridData(updatedData);
        setHasUnsaved(true);
    };

    const toggleAllRows = () => {
        const elements = Array.from(document.querySelectorAll(`${styles.level_1_z_tglb}`)) as HTMLDivElement[];
        if (isExpanded === false) {
            elements.forEach((el: HTMLDivElement) => {
                el.style.display = "none";
            });
        } else {
            elements.forEach((el: HTMLDivElement) => {
                el.style.display = "";
            });
        }
        elements.forEach((el: HTMLDivElement) => {
            el.style.display = el.style.display === "none" ? "" : "none";
        });

        setIsExpanded(!isExpanded);
    };

    const getInstructions = (fieldname: string) => {
        if (loggedInUser) {
            qGetInvInstrct(loggedInUser, group?.selectionId || "", gridData["contractor_vat_identification"], fieldname).then(res => {
                if (res) {
                    setIsInstructionCalloutOpen({
                        isOpen: true,
                        path: fieldname,
                        instructions: res
                    });
                } else {
                    toast.error("Error getting custom instruction");
                }
            });
        }
    };

    const toggleSection = (key: string) => {
        setSectionCollapseState(prev => {
            const newMap = new Map(prev); // clone to trigger re-render
            const current = newMap.get(key) ?? false;
            newMap.set(key, !current);
            localStorage.setItem(localStorageInvCollapsebleKey, JSON.stringify(Array.from(newMap.entries())));
            return newMap;
        });
    };

    const invInProgressWarnTooltip = useCallback(
        (map: Map<string, any>) => {
            return (((map.get('invoice_total_net')?.['missNet'] || 0) != 0 && (map.get('invoice_total_net')?.['totNet'] || 0)  != 0 ) || ((map.get('vat')?.['NetVatNotGrand'] || 0) != 0 && (map.get('vat')?.['totNet'] || 0)  != 0)) ? (
                <div className={styles.mismatchTooltioContainer}>
                    <h3 className={styles.mismatchContainerBorder}>What's wrong [beta]</h3>

                    {/* Item to Total Mismatch */}
                    {((map.get('invoice_total_net')?.['totNet'] || 0) != 0) && (

                        <div className={styles.cardRow}>
                            {/* Card 1 */}
                            <div className={styles.toolVerBoxWrap}>
                                <div className={styles.cardTitleError}>Net Total mismatches Items Sum</div>
                            </div>
                            {/* Card 2 */}
                            <div className={styles.toolVerBoxWrap}>
                                <div className={styles.cardTitle}>Net Total</div>
                                <h3 className={styles.cardValue}>
                                    {(map.get('invoice_total_net')?.['totNet'] || 0).toFixed(2)}
                                </h3>
                            </div>
                            {/* Card 3 */}
                            <div className={styles.toolVerBoxWrap}>
                                <div className={styles.cardTitle}>Item Sum</div>
                                <h3 className={styles.cardValue}>
                                    {(map.get('invoice_total_net')?.['calcItmTot'] || 0).toFixed(2)}
                                </h3>
                            </div>
                            {/* Card 4 */}
                            <div className={styles.toolVerBoxWrap}>
                                <div className={styles.cardTitle}>Diff</div>
                                <h3 className={styles.cardValue}>
                                    {(map.get('invoice_total_net')?.['missNet'] || 0).toFixed(2)}
                                </h3>
                            </div>
                        </div>
                    )}

                    {/* VAT Mismatch */}
                    {((map.get('vat')?.['NetVatNotGrand'] || 0) != 0 && (map.get('vat')?.['totNet'] || 0)  != 0 ) && (

                        <div className={styles.cardRow}>
                            {/* Card 1 */}
                            <div className={styles.toolVerBoxWrap}>
                                 <div className={styles.cardTitleError}>Net + VAT mismatches Gross Total</div>
                            </div>
                            {/* Card 2 */}
                            <div className={styles.toolVerBoxWrap}>
                                <div className={styles.cardTitle}>Gross Total</div>
                                <h3 className={styles.cardValue}>
                                    {(map.get('vat')?.['totGrss'] || 0).toFixed(2)}
                                </h3>
                            </div>
                            {/* Card 3 */}
                            <div className={styles.toolVerBoxWrap}>
                                <div className={styles.cardTitle}>Net+Vat</div>
                                <h3 className={styles.cardValue}>
                                    {(map.get('vat')?.['totNet'] || 0) + (map.get('vat')?.['vat'] || 0)}
                                </h3>
                            </div>
                            {/* Card 4 */}
                            <div className={styles.toolVerBoxWrap}>
                                <div className={styles.cardTitle}>Diff</div>
                                <h3 className={styles.cardValue}>
                                    {(map.get('vat')?.['NetVatNotGrand'] || 0).toFixed(2)}
                                </h3>
                            </div>
                        </div>
                    )}
                </div>

            ): (<div>Quick verification overview coming soon.</div>);
        }, [fid]
    );

    const renderGrid = (data: any, path: (string | number)[] = [], level = 0) => {
        {/* to add fields ⇛ mapping assigning fields to a section ⇒ invMainFieldToGroupMap */}

        return Object.entries(sortObjectByKeyOrder(data, orderListWithCustom))
            .filter(([key, value]) => !(key === "page" && level === 0))
            .sort(([key1, value1], [key2, value2]) => {
                if (key1 == "page") return orderListWithCustom.indexOf(key1) - orderListWithCustom.indexOf(key2);
                if (key1 == "item_number") return orderListWithCustom.indexOf(key1) - orderListWithCustom.indexOf(key1);
                return 0;
            })
            .map(([key, value]) => {
                const currentPath = [...path, key];
                const pathAsString = pathToBreadcrumb(currentPath);
                // modify column name (addressed by internal keys) -‼️rename some internal keys for display
                let key_rename = invoice_key_renames(key);
                return (
                    <TooltipHost key={`ii_${key}`} content={`${inv_main_tbl_tooltips[key] || ""}`} directionalHint={DirectionalHint.leftCenter} styles={invWarnDynTooltip}>
                        {/* section header */}
                        {collapsableGroups.has(key) && (
                            <div className={styles.dividerContainer} onClick={() => toggleSection(collapsableGroups.get(key) || "")}>
                                <div className={styles.dividerTextContainer}>
                                    <span className={styles.collapseExpandSign}>{sectionCollapseState.get(collapsableGroups.get(key) || "") == true ? "▾" : "▸"}</span>
                                    {collapsableGroups.get(key)} {totalsWarnInternalMap.size > 0 && key == "vat" && (
                                        <div className={styles.warningIconDiv}>
                                            <TooltipHost content={invInProgressWarnTooltip(totalsWarnInternalMap) || ""} styles={invWarnDynTooltip} delay={500 as any}>
                                                <div style={{ overflow: "visible", height: "0px", position: "relative", display: "flex", marginTop: "-12px" }}>
                                                    <TbMoodLookUp size={18} color="#d46b08"/>
                                                </div>
                                            </TooltipHost>
                                        </div>
                                    )}
                                </div>
                                <div className={styles.dividerInvGrid}></div>
                            </div>
                        )}

                        {fieldsGroupedWCust.has(key) && sectionCollapseState.get(fieldsGroupedWCust.get(key) || "") == false && (
                            <div
                                key={pathAsString}
                                id={pathAsString?.replace(/>/g, "-")}
                                className={`${
                                    fieldsTurnedOff.includes(key)
                                        ? `${styles.grid_row_z_hidden} ${styles[`level_${level}_z`]}`
                                        : invoice_togglebles.includes(key)
                                            ? `${styles.grid_row_z} ${styles[`level_${level}_z_tglb`]}`
                                            : `${styles.grid_row_z} ${styles[`level_${level}_z`]}`
                                    }`
                                }
                            >
                                {/* label */}
                                <div className={key != "invoice_items" ? `${styles.grid_cell_z} ${styles.key_z}` : `${styles.grid_cell_z} ${styles.key_items}`} data-original-key={key}>
                                    {/* modify column name - replace '_' with ' ' & capitalize first letter of each word */}
                                    {key_rename.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase())}
                                </div>
                                <div className={key !== "invoice_items" ? `${styles.grid_cell_z} ${styles.value_z}` : `${styles.grid_cell_z} ${styles.value_items}`} style={{margin: "0px", padding: "3px", display: "block", width: "100%"}}>
                                    {typeof value === "object" && value !== null ? (
                                        Array.isArray(value) && key != "page_type" ? (
                                            <div className={styles.array_container_z}>
                                                <div className={styles.array_cont_z_sub}>
                                                    <div className={styles.array_cont_z_sub_sub} >
                                                        <div className={styles.add_icon_z} onClick={() => handleAddItem(currentPath)}>
                                                            <IoAddCircleOutline size={18} />
                                                        </div>
                                                        <div
                                                            className={styles.customize_icon_z}
                                                            onClick={e => {
                                                                setInstructCalloutTargetElement(e.currentTarget);
                                                                getInstructions(pathToBreadcrumb(currentPath));
                                                            }}
                                                        >
                                                            <BiCustomize size={16} />
                                                        </div>
                                                    </div>

                                                    <div className={styles.expand_items_icon_z} onClick={() => {toggleAllRows()}}>
                                                        {isExpanded ? (
                                                            <FaChevronCircleUp size={22} color="#039590" />
                                                        ) : (
                                                            <FaChevronCircleDown size={22} color="#039590" />
                                                        )}
                                                    </div>
                                                </div>

                                                {value.map((item, index) => {
                                                    const arrayItemPath = [...currentPath, index];
                                                    const arrayItemPathAsString = pathToBreadcrumb(arrayItemPath);
                                                    return (
                                                        <div
                                                            key={arrayItemPathAsString}
                                                            className={`${(item?.["total_mismatch"] || false) == true ? styles.array_container_mismatched_z : styles.array_container_z}`}
                                                        >
                                                            <div className={styles.delete_icon_z} title="Delete Item" onClick={() => handleDelete(arrayItemPath)}>
                                                                <MdDelete size={15} />
                                                            </div>
                                                            <div
                                                                className={styles.item_expand_icon_z}
                                                                title="Toggle Compact View"
                                                                onClick={() => {toggleItems(arrayItemPathAsString?.replace(/>/g, "-"), invoice_togglebles)}}
                                                            >
                                                                <BsChevronExpand size={16} />
                                                            </div>
                                                            <div className={styles.gridRenderContainer}>
                                                                <div style={{ flex: 1 }}>{renderGrid(item, arrayItemPath, level + 1)}</div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div>{renderGrid(value, currentPath, level + 1)}</div>
                                        )
                                    ) : !invoice_non_editables.includes(key) ? (
                                        <div
                                            className={(key == 'invoice_total_net' && Math.abs(parseFloat(value as string) - (data?.['calculated_invoice_items_total'] + exciseSum)) > allowedDeviation) ?
                                                styles.grandTotalNotItemSum :(key && totalsWarnInternalMap.has(key)) ?
                                                    styles.vatWarnMild : styles.hideInCitationLink}>

                                            {/*  🔅🔅🔅 this is the editable field 🔅🔅🔅  */}

                                            <ValidateFieldWrap validation={getFieldValidation(data, key)}>
                                                <input
                                                    type="text"
                                                    className={styles.value_input_z}
                                                    value={typeof value === "string" || typeof value === "number" ? value : ""}
                                                    onChange={e => handleFieldChange(currentPath, e.target.value)}
                                                />
                                            </ValidateFieldWrap>
                                            {invMainFieldsWCitationLink.includes(key) && (
                                                <div className={styles.invGridCitationLink} onClick={onDataHighlight ? () => onDataHighlight(String(value)) : undefined}>
                                                    <BiSolidUpArrowSquare size={15}></BiSolidUpArrowSquare>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div
                                            className={(key == 'calculated_invoice_items_total' && Math.abs((parseFloat(value as string) + exciseSum) - data?.['invoice_total_net']) > allowedDeviation) ?
                                                styles.grandTotalNotItemSumForDsbld : (key && totalsWarnInternalMap.has(key)) ?
                                                    styles.vatWarnMild : styles.value_text_z}
                                                style={{cursor: onDataHighlight ? "pointer" : "default"}}
                                            onClick={onDataHighlight ? () => onDataHighlight(String(value)) : undefined}
                                        >
                                            <ValidateFieldWrap validation={getFieldValidation(data, key)}>
                                                <div style={{ minHeight: "23px", display: "flex", alignItems: "center", justifyContent: "center" }}>{String(value || "").trim()}</div>
                                            </ValidateFieldWrap>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </TooltipHost>
                );
            });
    };

    return (
        <div className={styles.mainContainer}>
            <div className={styles.invoiceDataTitle}>
                <span className={styles.invoiceDataTitleText}>
                    <div className={styles.invoiceFileNameAndDownloadButton}>
                        <div>Main Invoice Data</div>
                        <div>
                            <TooltipHost content={isVersionView ? "Unavailable while viewing a previous version" : "Download Invoice File"} directionalHint={DirectionalHint.topCenter}>
                                <button
                                    type="button"
                                    className={styles.downloadBtn}
                                    aria-label="Download invoice file"
                                    disabled={isVersionView === true}
                                    onClick={async (e) => {
                                        e.stopPropagation();
                                        if (isVersionView) return;
                                        if (!fid || !filename) return;
                                        try {
                                            await downloadPDF(
                                                getContentPathWithId(filename, fid),
                                                filename,
                                                group?.companyid || "",
                                                loggedInUser as gloggedinuser
                                            );
                                        } catch (err) {
                                            toast.error("Failed to download file");
                                        }
                                    }}
                                >
                                    <FiDownload size={14} />
                                </button>
                            </TooltipHost>
                            <TooltipHost content={isVersionView ? "Unavailable while viewing a previous version" : "Copy Invoice Link"} directionalHint={DirectionalHint.topCenter}>
                                <button
                                    type="button"
                                    className={styles.downloadBtn}
                                    aria-label="Copy Invoice Link"
                                    disabled={isVersionView === true}
                                    onClick={async (e) => {
                                        e.stopPropagation();
                                        if (isVersionView) return;
                                        if (!fid || !filename) return;
                                        try {
                                            if (group?.selectionId == null || group?.companyid == null) {
                                                toast.error("Incorrect data for generating invoice link");
                                                return;
                                            }
                                            let orig = window.location.origin
                                            if (import.meta.env.DEV === true ) orig = "https://app.kognify.ai"
                                            const link = `${orig}/#/${group?.companyid || ""}/${encodeURIComponent(group?.selectionId || "")}/library/${fid}`;
                                            await navigator.clipboard.writeText(link);
                                            toast.success("Invoice link copied to clipboard", { autoClose: 700 });
                                        } catch (err) {
                                            toast.error("Failed to copy invoice link");
                                        }
                                    }}
                                >
                                    <TbBrowserShare size={15} />
                                </button>
                            </TooltipHost>
                        </div>
                    </div>
                    <div>{fid}</div>
                </span>
            </div>

            {/*contractor id field*/}
            <TooltipHost content='Contractor ID for this invoice' directionalHint={DirectionalHint.leftCenter}>
                <div className={styles.leftTopHeadContractorID}>
                    <div className={styles.leftTopHeadContractorLabel}>Contractor VAT ID:</div> {gridData['contractor_vat_identification']}
                </div>
            </TooltipHost>

            {/*state*/}
            <TooltipHost content="Agentic AI recalculation status for this invoice." directionalHint={DirectionalHint.leftCenter}>
                <div className={styles.statusMessage}>
                    <div className={styles.leftTopHeadStateLabel}>State:</div>
                    <div className={styles.statusMessageText}>{gridData["status_message"]}</div>
                </div>
            </TooltipHost>

            {/*grid*/}
            {gridData && (
                <div style={{ fontFamily: "Urbanist, Roboto" }} className={styles.json_grid_z}>
                    {renderGrid(gridData)}
                </div>
            )}

            {/*save*/}
            <div>
                <div></div>
                <div className={styles.saveHeaderBut}>
                    {/*
                    <IconButton onClick={handleSaveAll} title={"Save Header Changes"} ariaLabel={"Save Header Changes"} styles={invMainSaveButtonStyles}>
                        <LuSave size={24} color="#00c6af" />
                    </IconButton>
                    */}
                </div>
            </div>
            <div className={styles.horizontalLineEnd}></div>
        </div>
    );
};

export default JsonGridInvoice;


